import { PERMISSION_CODES } from '@/constants/permissions';
import { hasModulePermission } from '@/config/permissionConfig';

export interface RouteAccess {
  [key: string]: boolean;
}

/**
 * Dynamic access control configuration for routes and features
 * Uses permissions loaded from backend
 */
export default function access(
  initialState:
    | {
        currentUser?: any; // Changed from API.CurrentUser due to missing global type
        permissions?: string[];
      }
    | undefined,
): RouteAccess {
  const permissions = initialState?.permissions || [];

  // Log permissions for debugging (only in development)
  if (process.env.NODE_ENV === 'development') {
    console.debug('[Access Control] User permissions:', permissions);
    console.debug('[Access Control] Permission count:', permissions.length);
    console.debug('[Access Control] User data:', initialState?.currentUser);
    console.debug('[Access Control] Initial state full:', initialState);
  }

  const hasPermission = (code: string): boolean => {
    const hasAccess = permissions.some((permission) => {
      if (permission === code) {
        return true;
      }

      // Compatibility fallback for scoped or suffixed permission payloads.
      // Example: "order:view:all" or "order:view|all" should satisfy "order:view".
      return permission.startsWith(`${code}:`) || permission.startsWith(`${code}|`);
    });

    if (!hasAccess && process.env.NODE_ENV === 'development') {
      console.debug(`[Access Control] Permission check failed for: ${code}`);
    }

    return hasAccess;
  };

  const hasAnyPermission = (codes: string[]): boolean => {
    const hasAccess = codes.some((code) => hasPermission(code));
    if (!hasAccess && process.env.NODE_ENV === 'development') {
      console.debug(`[Access Control] No permissions found from: ${codes.join(', ')}`);
    }
    return hasAccess;
  };

  const canViewWarehouseStructureAnalysis = hasPermission(
    PERMISSION_CODES.WAREHOUSE_STRUCTURE_ANALYSIS_VIEW,
  );
  const canViewStoreStructureAnalysis = hasPermission(
    PERMISSION_CODES.STORE_STRUCTURE_ANALYSIS_VIEW,
  );
  const canViewInventoryExpiryAnalysis = hasPermission(
    PERMISSION_CODES.INVENTORY_EXPIRY_ANALYSIS_VIEW,
  );
  const canViewInventoryStoreAnalysis = hasPermission(
    PERMISSION_CODES.INVENTORY_STORE_ANALYSIS_VIEW,
  );
  const canViewInventoryUnsoldProducts = hasPermission(
    PERMISSION_CODES.INVENTORY_UNSOLD_PRODUCTS_VIEW,
  );
  const canExportInventoryUnsoldProducts = hasPermission(
    PERMISSION_CODES.INVENTORY_UNSOLD_PRODUCTS_EXPORT,
  );
  const canViewInventoryTurnoverAnalysis = hasPermission(
    PERMISSION_CODES.INVENTORY_TURNOVER_ANALYSIS_VIEW,
  );
  const canViewBrands = hasAnyPermission([
    PERMISSION_CODES.BRAND_VIEW,
    PERMISSION_CODES.BRAND_CREATE_EDIT,
    PERMISSION_CODES.BRAND_DELETE,
  ]);

  return {
    // Finance
    financeModule: hasModulePermission('financeModule', permissions),
    canViewFinanceOverview: hasPermission(PERMISSION_CODES.FINANCE_OVERVIEW_VIEW),
    canViewFinanceSetup: hasPermission(PERMISSION_CODES.FINANCE_SETUP_VIEW),
    canManageFinanceSetup: hasPermission(PERMISSION_CODES.FINANCE_SETUP_MANAGE),
    canViewFinanceBankSync: hasPermission(PERMISSION_CODES.FINANCE_BANK_SYNC_VIEW),
    canViewFinanceAudit: hasPermission(PERMISSION_CODES.FINANCE_AUDIT_VIEW),
    // This combined page loads both account summaries and transaction detail.
    // Require both read permissions instead of exposing a half-authorized page.
    canViewFinanceBanking:
      hasPermission(PERMISSION_CODES.FINANCE_BANK_ACCOUNTS_VIEW) &&
      hasPermission(PERMISSION_CODES.FINANCE_BANK_TRANSACTIONS_VIEW),
    canViewFinanceBankTransactions: hasPermission(PERMISSION_CODES.FINANCE_BANK_TRANSACTIONS_VIEW),
    canExportFinanceBankTransactions: hasPermission(
      PERMISSION_CODES.FINANCE_BANK_TRANSACTIONS_EXPORT,
    ),
    canViewFinanceCoa: hasPermission(PERMISSION_CODES.FINANCE_COA_VIEW),
    canManageFinanceCoa: hasPermission(PERMISSION_CODES.FINANCE_COA_MANAGE),
    canViewFinanceReconciliation: hasPermission(PERMISSION_CODES.FINANCE_RECONCILIATION_VIEW),
    canApplyFinanceReconciliation: hasPermission(PERMISSION_CODES.FINANCE_RECONCILIATION_APPLY),
    canReverseFinanceReconciliation: hasPermission(PERMISSION_CODES.FINANCE_RECONCILIATION_REVERSE),
    canViewFinanceJournals: hasPermission(PERMISSION_CODES.FINANCE_JOURNAL_VIEW),
    canCreateFinanceJournal: hasPermission(PERMISSION_CODES.FINANCE_JOURNAL_CREATE),
    canApproveFinanceJournal: hasPermission(PERMISSION_CODES.FINANCE_JOURNAL_APPROVE),
    canPostFinanceJournal: hasPermission(PERMISSION_CODES.FINANCE_JOURNAL_POST),
    canReverseFinanceJournal: hasPermission(PERMISSION_CODES.FINANCE_JOURNAL_REVERSE),
    canViewFinanceReports: hasPermission(PERMISSION_CODES.FINANCE_REPORTS_VIEW),
    canExportFinanceReports: hasPermission(PERMISSION_CODES.FINANCE_REPORTS_EXPORT),

    // Product Management
    canViewProducts: hasPermission(PERMISSION_CODES.PRODUCT_VIEW),
    canViewProductDescriptionManagement: hasPermission(
      PERMISSION_CODES.PRODUCT_DESCRIPTION_MANAGE_VIEW,
    ),
    canEditProductDescriptionManagement: hasPermission(
      PERMISSION_CODES.PRODUCT_DESCRIPTION_MANAGE_EDIT,
    ),
    canViewProductCost: hasPermission(PERMISSION_CODES.PRODUCT_VIEW_COST),
    canCreateEditProduct: hasPermission(PERMISSION_CODES.PRODUCT_CREATE_EDIT),
    canEditProductCost: hasPermission(PERMISSION_CODES.PRODUCT_EDIT_COST),
    canEditProductPrice: hasPermission(PERMISSION_CODES.PRODUCT_EDIT_PRICE),
    canExportProducts: hasPermission(PERMISSION_CODES.PRODUCT_EXPORT),
    canDeleteProduct: hasPermission(PERMISSION_CODES.PRODUCT_DELETE),
    canViewProductDisplays: hasPermission(PERMISSION_CODES.PRODUCT_DISPLAYS_VIEW),
    canEditProductDisplays: hasPermission(PERMISSION_CODES.PRODUCT_DISPLAYS_EDIT),
    canViewProductChangeLogs: hasPermission(PERMISSION_CODES.PRODUCT_CHANGE_LOG_VIEW),
    canViewHanshowElectronicShelfLabels: hasPermission(PERMISSION_CODES.HANSHOW_ESL_VIEW),
    canManageHanshowElectronicShelfLabels: hasPermission(PERMISSION_CODES.HANSHOW_ESL_MANAGE),
    canViewShipmentSpec: hasPermission(PERMISSION_CODES.SHIPMENT_SPEC_VIEW),
    canViewNoBarcodeProducts: hasAnyPermission([
      PERMISSION_CODES.NO_BARCODE_PRODUCT_VIEW,
      PERMISSION_CODES.NO_BARCODE_PRODUCT_MANAGE,
    ]),
    canViewExpiredProductManagement: hasPermission(
      PERMISSION_CODES.EXPIRED_PRODUCT_MANAGEMENT_VIEW,
    ),
    canViewUnsalableProductReport: hasPermission(PERMISSION_CODES.UNSALABLE_PRODUCT_REPORT_VIEW),
    canCreateUnsalableProductReport: hasPermission(
      PERMISSION_CODES.UNSALABLE_PRODUCT_REPORT_CREATE,
    ),
    canFollowUpUnsalableProductReport: hasPermission(
      PERMISSION_CODES.UNSALABLE_PRODUCT_REPORT_FOLLOW_UP,
    ),
    canViewCarryoverProductManagement: hasPermission(PERMISSION_CODES.NEW_ARRIVAL_MANAGEMENT_VIEW),
    canManageNoBarcodeProducts: hasPermission(PERMISSION_CODES.NO_BARCODE_PRODUCT_MANAGE),
    canViewOnlineProducts: hasPermission(PERMISSION_CODES.ONLINE_PRODUCT_VIEW),
    canCreateOnlineProducts: hasPermission(PERMISSION_CODES.ONLINE_PRODUCT_CREATE),
    canApproveOnlineProducts: hasPermission(PERMISSION_CODES.ONLINE_PRODUCT_APPROVE),
    // Discount
    canViewDiscount: hasPermission(PERMISSION_CODES.DISCOUNT_VIEW),
    canCreateEditDiscount: hasPermission(PERMISSION_CODES.DISCOUNT_CREATE_EDIT),
    canDeleteDiscount: hasPermission(PERMISSION_CODES.DISCOUNT_DELETE),

    // Featured Product Management
    canViewFeaturedProducts: hasPermission(PERMISSION_CODES.FEATURED_PRODUCT_VIEW),

    // Traffic Product Management (removed; merged into Supply Product Management)
    canViewBannedProducts: hasPermission(PERMISSION_CODES.BANNED_PRODUCTS_VIEW),
    canEditBannedProducts: hasPermission(PERMISSION_CODES.BANNED_PRODUCTS_EDIT),

    // Supply Product Management
    canViewSupplyProducts: hasPermission(PERMISSION_CODES.SUPPLY_PRODUCT_VIEW),

    // Replenishment Management
    canViewReplenishment: hasPermission(PERMISSION_CODES.REPLENISHMENT_VIEW),

    // Order
    canViewOrders:
      hasPermission(PERMISSION_CODES.ORDER_VIEW) ||
      hasPermission(PERMISSION_CODES.ORDER_RECONCILIATION_VIEW) ||
      hasPermission(PERMISSION_CODES.WHOLESALE_ORDER_VIEW) ||
      hasPermission(PERMISSION_CODES.EMPLOYEE_PURCHASE_REFUND_VIEW) ||
      hasPermission(PERMISSION_CODES.EMPLOYEE_PURCHASE_REFUND_APPROVE),
    canViewOrderList: hasPermission(PERMISSION_CODES.ORDER_VIEW),
    canViewOrderReconciliation: hasPermission(PERMISSION_CODES.ORDER_RECONCILIATION_VIEW),
    canViewWholesaleOrders: hasPermission(PERMISSION_CODES.WHOLESALE_ORDER_VIEW),
    canViewOnlineOrders: hasPermission(PERMISSION_CODES.ONLINE_ORDER_VIEW),
    canViewOnlineOrderFollowup: hasPermission(PERMISSION_CODES.ONLINE_ORDER_FOLLOWUP_VIEW),
    canRefundOrders: hasPermission(PERMISSION_CODES.ORDER_REFUND),
    canCashRefundOnlineOrders: hasPermission(PERMISSION_CODES.ONLINE_ORDER_CASH_REFUND),
    canOriginalRefundOnlineOrders: hasPermission(PERMISSION_CODES.ONLINE_ORDER_ORIGINAL_REFUND),
    canViewOnlineStoreDiscountCodes: hasPermission(
      PERMISSION_CODES.ONLINE_STORE_DISCOUNT_CODE_VIEW,
    ),
    canViewOnlineStoreCustomerMessages: hasPermission(
      PERMISSION_CODES.ONLINE_STORE_CUSTOMER_MESSAGE_VIEW,
    ),
    canViewOnlineStoreEmailNotifications: hasPermission(
      PERMISSION_CODES.ONLINE_STORE_EMAIL_NOTIFICATION_VIEW,
    ),
    canViewOnlineStoreBlogPosts: hasPermission(PERMISSION_CODES.ONLINE_STORE_BLOG_POST_VIEW),
    canCreateEditOnlineStoreBlogPosts: hasPermission(
      PERMISSION_CODES.ONLINE_STORE_BLOG_POST_CREATE_EDIT,
    ),
    canDeleteOnlineStoreBlogPosts: hasPermission(PERMISSION_CODES.ONLINE_STORE_BLOG_POST_DELETE),
    canViewMarketingManagement: hasAnyPermission([
      PERMISSION_CODES.MARKETING_MANAGEMENT_VIEW,
      PERMISSION_CODES.MARKETING_MANAGEMENT_MANAGE,
    ]),
    canManageMarketingManagement: hasPermission(PERMISSION_CODES.MARKETING_MANAGEMENT_MANAGE),
    canEditOnlineOrders: hasPermission(PERMISSION_CODES.ONLINE_ORDER_EDIT),
    canCreateWholesaleOrders: hasPermission(PERMISSION_CODES.WHOLESALE_ORDER_CREATE),

    // Purchase
    canViewPurchaseOpportunity: hasPermission(PERMISSION_CODES.PURCHASE_OPPORTUNITY_VIEW),
    canViewProcurementGoals: hasPermission(PERMISSION_CODES.PURCHASE_PROCUREMENT_GOAL_VIEW),
    canCreateEditProcurementGoals: hasPermission(
      PERMISSION_CODES.PURCHASE_PROCUREMENT_GOAL_CREATE_EDIT,
    ),
    canFollowProcurementGoals: hasPermission(PERMISSION_CODES.PURCHASE_PROCUREMENT_GOAL_FOLLOW),
    canCompleteProcurementGoals: hasPermission(PERMISSION_CODES.PURCHASE_PROCUREMENT_GOAL_COMPLETE),
    canCreateEditPurchaseOpportunity: hasPermission(
      PERMISSION_CODES.PURCHASE_OPPORTUNITY_CREATE_EDIT,
    ),
    canDeletePurchaseOpportunity: hasPermission(PERMISSION_CODES.PURCHASE_OPPORTUNITY_DELETE),
    canReviewPurchaseOpportunityCannotContact: hasPermission(
      PERMISSION_CODES.PURCHASE_OPPORTUNITY_CANNOT_CONTACT_REVIEW,
    ),
    canViewPurchaseQuote: hasPermission(PERMISSION_CODES.PURCHASE_QUOTE_VIEW),
    canCreateEditPurchaseQuote: hasPermission(PERMISSION_CODES.PURCHASE_QUOTE_CREATE_EDIT),
    canDeletePurchaseQuote: hasPermission(PERMISSION_CODES.PURCHASE_QUOTE_DELETE),
    canViewAiPurchaseImport: hasPermission(PERMISSION_CODES.AI_PURCHASE_IMPORT_VIEW),
    canCreateEditAiPurchaseImport: hasPermission(PERMISSION_CODES.AI_PURCHASE_IMPORT_CREATE_EDIT),
    canViewPurchaseOrder: hasPermission(PERMISSION_CODES.PURCHASE_ORDER_VIEW),
    canViewMarketPriceSearch: hasPermission(PERMISSION_CODES.PURCHASE_MARKET_PRICE_SEARCH_VIEW),
    canViewGreenpriceCatalog: hasPermission(PERMISSION_CODES.PURCHASE_GREENPRICE_CATALOG_VIEW),
    canViewPriceCompetitiveness: hasPermission(
      PERMISSION_CODES.PURCHASE_PRICE_COMPETITIVENESS_VIEW,
    ),
    canViewPurchaseOrderArrivalSchedule: hasAnyPermission([
      PERMISSION_CODES.PURCHASE_ORDER_ARRIVAL_SCHEDULE_VIEW,
      PERMISSION_CODES.PURCHASE_ORDER_ARRIVAL_SCHEDULE_FINANCE_RECONCILIATION,
    ]),
    canViewPurchaseCategoryArrivalCalendar: hasPermission(
      PERMISSION_CODES.PURCHASE_CATEGORY_ARRIVAL_CALENDAR_VIEW,
    ),
    canEditPurchaseOrderArrivalSchedule: hasPermission(
      PERMISSION_CODES.PURCHASE_ORDER_ARRIVAL_SCHEDULE_EDIT,
    ),
    canFinanceReconcilePurchaseOrderArrivalSchedule: hasPermission(
      PERMISSION_CODES.PURCHASE_ORDER_ARRIVAL_SCHEDULE_FINANCE_RECONCILIATION,
    ),
    canCreateEditPurchaseOrder: hasPermission(PERMISSION_CODES.PURCHASE_ORDER_CREATE_EDIT),
    canDeletePurchaseOrder: hasPermission(PERMISSION_CODES.PURCHASE_ORDER_DELETE),
    canRequestPurchaseOrderPayment: hasPermission(PERMISSION_CODES.PURCHASE_ORDER_REQUEST_PAYMENT),
    canRequestPurchaseOrderModify: hasPermission(PERMISSION_CODES.PURCHASE_ORDER_REQUEST_MODIFY),
    canViewPurchaseOrderModifyApproval: hasPermission(
      PERMISSION_CODES.PURCHASE_ORDER_MODIFY_APPROVAL_VIEW,
    ),
    canApprovePurchaseOrderModify: hasPermission(PERMISSION_CODES.PURCHASE_ORDER_MODIFY_APPROVE),
    canViewPurchaseOrderPayment: hasPermission(PERMISSION_CODES.PURCHASE_ORDER_PAYMENT_VIEW),
    canViewPurchaseOrderPaymentVoucher: hasPermission(
      PERMISSION_CODES.PURCHASE_ORDER_PAYMENT_VOUCHER_VIEW,
    ),
    canViewPurchaseOrderFinanceManagement: hasPermission(
      PERMISSION_CODES.PURCHASE_ORDER_FINANCE_MANAGEMENT_VIEW,
    ),
    canViewPurchaseOrderPaymentVarianceReview: hasPermission(
      PERMISSION_CODES.PURCHASE_ORDER_PAYMENT_VARIANCE_REVIEW_VIEW,
    ),
    canViewPurchaseOrderHandlingFollowup: hasPermission(
      PERMISSION_CODES.PURCHASE_ORDER_HANDLING_FOLLOWUP_VIEW,
    ),
    canExecutePurchaseOrderPayment: hasPermission(PERMISSION_CODES.PURCHASE_ORDER_PAYMENT_EXECUTE),
    canViewLogisticsFeeManagement: hasPermission(PERMISSION_CODES.LOGISTICS_FEE_MANAGEMENT_VIEW),
    canApproveLogisticsFeePayment: hasPermission(PERMISSION_CODES.LOGISTICS_FEE_APPROVE_PAYMENT),
    canApprovePurchaseQuote: hasPermission(PERMISSION_CODES.PURCHASE_QUOTE_APPROVE),
    canViewEmployeePurchaseRefund:
      hasPermission(PERMISSION_CODES.EMPLOYEE_PURCHASE_REFUND_VIEW) ||
      hasPermission(PERMISSION_CODES.EMPLOYEE_PURCHASE_REFUND_APPROVE),
    canApproveEmployeePurchaseRefund: hasPermission(
      PERMISSION_CODES.EMPLOYEE_PURCHASE_REFUND_APPROVE,
    ),
    canViewPurchaseAnalysis: hasPermission(PERMISSION_CODES.PROCUREMENT_ANALYSIS_MODULE_VIEW),
    canExportProcurementAnalysis: hasPermission(
      PERMISSION_CODES.PROCUREMENT_ANALYSIS_MODULE_EXPORT,
    ),
    canViewProcurementDashboard: hasPermission(
      PERMISSION_CODES.PROCUREMENT_ANALYSIS_DASHBOARD_VIEW,
    ),
    canViewPurchaseDataErrors: hasPermission(
      PERMISSION_CODES.PROCUREMENT_ANALYSIS_DATA_ERRORS_VIEW,
    ),
    canViewPurchaseHistoryPerformance: hasPermission(
      PERMISSION_CODES.PROCUREMENT_ANALYSIS_HISTORY_PERFORMANCE_VIEW,
    ),

    // Supplier
    canViewSuppliers: hasPermission(PERMISSION_CODES.SUPPLIER_VIEW),
    canCreateEditSupplier: hasPermission(PERMISSION_CODES.SUPPLIER_CREATE_EDIT),
    canDeleteSupplier: hasPermission(PERMISSION_CODES.SUPPLIER_DELETE),
    canViewSupplierDevelopment: hasPermission(PERMISSION_CODES.SUPPLIER_DEVELOPMENT_VIEW),
    canCreateEditSupplierDevelopment: hasPermission(
      PERMISSION_CODES.SUPPLIER_DEVELOPMENT_CREATE_EDIT,
    ),
    canLinkConvertSupplierDevelopment: hasPermission(
      PERMISSION_CODES.SUPPLIER_DEVELOPMENT_LINK_CONVERT,
    ),
    canViewSupplierPlatformGroups: hasAnyPermission([
      PERMISSION_CODES.SUPPLIER_PLATFORM_GROUP_VIEW,
      PERMISSION_CODES.SUPPLIER_PLATFORM_MESSAGE_VIEW,
    ]),
    canMatchSupplierPlatformGroups: hasPermission(PERMISSION_CODES.SUPPLIER_PLATFORM_GROUP_MATCH),
    canViewSupplierPlatformMessages: hasPermission(PERMISSION_CODES.SUPPLIER_PLATFORM_MESSAGE_VIEW),
    canFollowUpSupplierPlatformMessages: hasPermission(
      PERMISSION_CODES.SUPPLIER_PLATFORM_MESSAGE_FOLLOW_UP,
    ),
    canViewSupplierFollowUp: hasPermission(PERMISSION_CODES.SUPPLIER_FOLLOW_UP_VIEW),
    canManageSupplierFollowUp: hasPermission(PERMISSION_CODES.SUPPLIER_FOLLOW_UP_MANAGE),
    canViewSupplierCalls: hasPermission(PERMISSION_CODES.SUPPLIER_CALLS_VIEW),
    canCreateSupplierCalls: hasPermission(PERMISSION_CODES.SUPPLIER_CALLS_CREATE),
    canUpdateSupplierCallNotes: hasPermission(PERMISSION_CODES.SUPPLIER_CALLS_UPDATE_NOTES),
    canListenSupplierCallRecording: hasPermission(PERMISSION_CODES.SUPPLIER_CALLS_LISTEN_RECORDING),

    // Brand
    canViewBrands,
    canCreateEditBrand: hasPermission(PERMISSION_CODES.BRAND_CREATE_EDIT),
    canDeleteBrand: hasPermission(PERMISSION_CODES.BRAND_DELETE),

    // Category
    canViewCategories: hasPermission(PERMISSION_CODES.CATEGORY_VIEW),
    canViewCategoryReview: hasPermission('category_review:view'),
    canEditCategoryReview: hasPermission('category_review:edit'),
    canApproveCategoryReview: hasPermission('category_review:approve'),

    // Store
    canViewStores: (() => {
      if (
        initialState?.currentUser?.role?.name === 'admin' ||
        initialState?.currentUser?.access === 'admin' ||
        initialState?.currentUser?.role?.code === 'admin'
      ) {
        return true;
      }
      return hasPermission(PERMISSION_CODES.STORE_VIEW);
    })(),
    canCreateEditStore: (() => {
      if (
        initialState?.currentUser?.role?.name === 'admin' ||
        initialState?.currentUser?.access === 'admin' ||
        initialState?.currentUser?.role?.code === 'admin'
      ) {
        return true;
      }
      return hasPermission(PERMISSION_CODES.STORE_CREATE_EDIT);
    })(),
    canDeleteStore: (() => {
      if (
        initialState?.currentUser?.role?.name === 'admin' ||
        initialState?.currentUser?.access === 'admin' ||
        initialState?.currentUser?.role?.code === 'admin'
      ) {
        return true;
      }
      return hasPermission(PERMISSION_CODES.STORE_DELETE);
    })(),
    canViewStoreRent: hasPermission(PERMISSION_CODES.STORE_RENT_VIEW),
    canManageStoreRent: hasPermission(PERMISSION_CODES.STORE_RENT_MANAGE),
    canRequestStoreRentPayment: hasPermission(PERMISSION_CODES.STORE_RENT_PAYMENT_REQUEST),
    canExecuteStoreRentPayment: hasPermission(PERMISSION_CODES.STORE_RENT_PAYMENT_EXECUTE),
    // Store Targets
    canViewStoreTargets: hasPermission(PERMISSION_CODES.STORE_TARGETS_VIEW),
    canEditStoreTargets: hasPermission(PERMISSION_CODES.STORE_TARGETS_EDIT),

    // Category Placement
    canViewCategoryPlacement: hasPermission(PERMISSION_CODES.CATEGORY_PLACEMENT_VIEW),
    canCreateEditCategoryPlacement: hasPermission(PERMISSION_CODES.CATEGORY_PLACEMENT_CREATE_EDIT),
    canApproveCategoryPlacement: hasPermission(PERMISSION_CODES.CATEGORY_PLACEMENT_APPROVE),

    // BEA Config Management
    canViewBeaConfig:
      hasPermission(PERMISSION_CODES.POS_BEA_CONFIG_VIEW) ||
      hasPermission(PERMISSION_CODES.POS_BEA_CONFIG_EDIT),
    canEditBeaConfig: hasPermission(PERMISSION_CODES.POS_BEA_CONFIG_EDIT),

    // Inventory
    canViewInventoryOverview: hasPermission(PERMISSION_CODES.INVENTORY_OVERVIEW_VIEW),
    canViewInventoryAnomalyDashboard: hasPermission(
      PERMISSION_CODES.INVENTORY_ANOMALY_DASHBOARD_VIEW,
    ),
    canViewInventoryHistory: hasPermission(PERMISSION_CODES.INVENTORY_HISTORY_VIEW),
    canViewInventoryChangeLog: hasPermission(PERMISSION_CODES.INVENTORY_CHANGE_LOG_VIEW),
    canViewInventoryTaskQueue: hasPermission(PERMISSION_CODES.INVENTORY_TASK_QUEUE_VIEW),
    canViewInventoryCheckFollowUps: hasPermission(PERMISSION_CODES.INVENTORY_CHECK_FOLLOWUPS_VIEW),
    canViewInventoryLoss: hasPermission(PERMISSION_CODES.INVENTORY_LOSS_VIEW),
    canViewInventoryFinancialLossDashboard: hasPermission(
      PERMISSION_CODES.INVENTORY_FINANCIAL_LOSS_DASHBOARD_VIEW,
    ),
    canViewProductDamageReports: hasPermission(PERMISSION_CODES.PRODUCT_DAMAGE_REPORT_VIEW),
    canSyncProductDamageReports: hasPermission(PERMISSION_CODES.PRODUCT_DAMAGE_REPORT_SYNC),
    canReceiveInventory: hasPermission(PERMISSION_CODES.INVENTORY_RECEIVE),
    canReduceReceivedInventory: hasPermission(PERMISSION_CODES.INVENTORY_REDUCE_RECEIVED_QUANTITY),
    canViewPurchaseReceiptConfirmations: hasPermission(
      PERMISSION_CODES.WAREHOUSE_PURCHASE_RECEIPT_VIEW,
    ),
    canConfirmPurchaseReceipt: hasPermission(PERMISSION_CODES.WAREHOUSE_PURCHASE_RECEIPT_CONFIRM),
    canViewStocktake: hasPermission(PERMISSION_CODES.STOCKTAKE_VIEW),
    canCreateEditStocktake: hasPermission(PERMISSION_CODES.STOCKTAKE_CREATE_EDIT),
    canDeleteStocktake: hasPermission(PERMISSION_CODES.STOCKTAKE_DELETE),
    canViewPurchaseOrders: hasPermission(PERMISSION_CODES.PURCHASE_ORDER_VIEW),
    'warehouse_structure_analysis:view': canViewWarehouseStructureAnalysis,
    'store_structure_analysis:view': canViewStoreStructureAnalysis,
    'inventory_expiry_analysis:view': canViewInventoryExpiryAnalysis,
    'inventory_store_analysis:view': canViewInventoryStoreAnalysis,
    'inventory_unsold_products:view': canViewInventoryUnsoldProducts,
    'inventory:unsold_products:export': canExportInventoryUnsoldProducts,
    'inventory_turnover_analysis:view': canViewInventoryTurnoverAnalysis,

    // Script
    canViewScripts: hasPermission(PERMISSION_CODES.SCRIPT_VIEW),
    canViewScriptExecutionLog: hasPermission(PERMISSION_CODES.SCRIPT_EXECUTION_LOG_VIEW),

    // Data Anomaly
    canViewDataChecks: hasPermission(PERMISSION_CODES.DATA_CHECKS_VIEW),
    canViewDuplicateBarcode: hasPermission(PERMISSION_CODES.DUPLICATE_BARCODE_VIEW),
    canViewProductExpiryDateErrors:
      hasPermission(PERMISSION_CODES.PRODUCT_EXPIRY_DATE_ERROR_VIEW) ||
      hasPermission(PERMISSION_CODES.PRODUCT_EXPIRY_DATE_ERROR_APPROVE),
    canApproveProductExpiryDateError: hasPermission(
      PERMISSION_CODES.PRODUCT_EXPIRY_DATE_ERROR_APPROVE,
    ),
    canViewMissingCategories: hasPermission(PERMISSION_CODES.MISSING_CATEGORIES_VIEW),
    canViewCategoryErrors: hasPermission(PERMISSION_CODES.CATEGORY_ERRORS_VIEW),
    canViewMissingBrand: hasPermission(PERMISSION_CODES.MISSING_BRAND_VIEW),
    canUpdateMissingBrand: hasPermission(PERMISSION_CODES.MISSING_BRAND_UPDATE),
    canViewMissingDescription: hasPermission(PERMISSION_CODES.MISSING_DESCRIPTION_VIEW),
    canUpdateMissingDescription: hasPermission(PERMISSION_CODES.MISSING_DESCRIPTION_UPDATE),
    canViewBadProductNames: hasPermission(PERMISSION_CODES.BAD_PRODUCT_NAME_VIEW),
    canUpdateBadProductNames: hasPermission(PERMISSION_CODES.BAD_PRODUCT_NAME_UPDATE),
    canManageProductStockType: hasPermission(PERMISSION_CODES.PRODUCT_STOCK_TYPE_MANAGEMENT),
    canManageDuplicateProducts: hasPermission(PERMISSION_CODES.DUPLICATE_PRODUCTS_MANAGE),

    // Sales Analysis
    canViewDataDashboard: hasPermission(PERMISSION_CODES.DATA_DASHBOARD_VIEW),
    canViewProductSalesAnalysis: hasPermission(PERMISSION_CODES.PRODUCT_SALES_ANALYSIS_VIEW),
    canViewProductSpecificSales: hasPermission(PERMISSION_CODES.PRODUCT_SPECIFIC_SALES_VIEW),
    canAssignProductSpecificSales: hasPermission(PERMISSION_CODES.PRODUCT_SPECIFIC_SALES_ASSIGN),
    canViewCategorySales: hasPermission(PERMISSION_CODES.CATEGORY_SALES_VIEW),
    canViewCategorySalesCharts: hasPermission(PERMISSION_CODES.CATEGORY_SALES_CHART_VIEW),
    canExportCategorySales: hasPermission(PERMISSION_CODES.CATEGORY_SALES_EXPORT),
    canViewPaymentGatewayAnalysis: hasPermission(PERMISSION_CODES.PAYMENT_GATEWAY_VIEW),
    canViewSupplierSalesReport: hasPermission(PERMISSION_CODES.SUPPLIER_SALES_REPORT_VIEW),
    canExportSupplierSalesReport: hasPermission(PERMISSION_CODES.SUPPLIER_SALES_REPORT_EXPORT),
    canViewFranchiseStoreSalesAnalysis: hasPermission(
      PERMISSION_CODES.FRANCHISE_STORE_SALES_ANALYSIS_VIEW,
    ),
    canViewOnlineStoreBehaviour: hasPermission(PERMISSION_CODES.ONLINE_STORE_BEHAVIOUR_VIEW),
    // Deprecated - kept for backward compatibility during migration
    canViewGrossMarginAnalysis: hasPermission(PERMISSION_CODES.CATEGORY_SALES_VIEW),

    // POS System
    canViewStoreAccount: hasPermission(PERMISSION_CODES.STORE_ACCOUNT_VIEW),
    canCreateEditStoreAccount: hasPermission(PERMISSION_CODES.STORE_ACCOUNT_CREATE_EDIT),
    canDeleteStoreAccount: hasPermission(PERMISSION_CODES.STORE_ACCOUNT_DELETE),
    canViewPdaOperationLogs: hasPermission(PERMISSION_CODES.PDA_OPERATION_LOG_VIEW),
    canViewPdaAccount: hasPermission(PERMISSION_CODES.PDA_ACCOUNT_VIEW),
    canCreateEditPdaAccount: hasPermission(PERMISSION_CODES.PDA_ACCOUNT_CREATE_EDIT),
    canDeletePdaAccount: hasPermission(PERMISSION_CODES.PDA_ACCOUNT_DELETE),
    canViewPaymentMethod: hasPermission(PERMISSION_CODES.PAYMENT_METHOD_VIEW),
    canViewPosAppVersion: hasPermission(PERMISSION_CODES.POS_APP_VERSION_VIEW),
    canCreateEditPosAppVersion: hasPermission(PERMISSION_CODES.POS_APP_VERSION_CREATE_EDIT),
    canDeletePosAppVersion: hasPermission(PERMISSION_CODES.POS_APP_VERSION_DELETE),
    canViewPosSession: hasPermission(PERMISSION_CODES.POS_SESSION_VIEW),
    canViewPosOperationLogs: hasPermission(PERMISSION_CODES.POS_OPERATION_LOG_VIEW),
    canViewPosReceiptContent: hasPermission(PERMISSION_CODES.POS_RECEIPT_CONTENT_VIEW),
    canViewPosQuickActions: hasPermission(PERMISSION_CODES.POS_QUICK_ACTION_VIEW),

    // Market Info
    canViewCollectionTask: hasPermission(PERMISSION_CODES.COLLECTION_TASK_VIEW),
    canCreateEditCollectionTask: hasPermission(PERMISSION_CODES.COLLECTION_TASK_CREATE_EDIT),
    canDeleteCollectionTask: hasPermission(PERMISSION_CODES.COLLECTION_TASK_DELETE),
    canViewMiProduct: hasPermission(PERMISSION_CODES.MI_PRODUCT_VIEW),
    canEditMiProduct: hasPermission(PERMISSION_CODES.MI_PRODUCT_EDIT),
    canDeleteMiProduct: hasPermission(PERMISSION_CODES.MI_PRODUCT_DELETE),
    canViewDataReview: hasPermission(PERMISSION_CODES.DATA_REVIEW_VIEW),
    canEditApproveDataReview: hasPermission(PERMISSION_CODES.DATA_REVIEW_EDIT_APPROVE),
    canViewDataAnalysis: hasPermission(PERMISSION_CODES.DATA_ANALYSIS_VIEW),
    canViewPriceAnalysis: hasPermission(PERMISSION_CODES.PRICE_ANALYSIS_VIEW),
    canViewMarketProductAnalysis: hasPermission(PERMISSION_CODES.MARKET_PRODUCT_ANALYSIS_VIEW),
    canViewMiUser: hasPermission(PERMISSION_CODES.MI_USER_VIEW),
    canCreateEditMiUser: hasPermission(PERMISSION_CODES.MI_USER_CREATE_EDIT),
    canDeleteMiUser: hasPermission(PERMISSION_CODES.MI_USER_DELETE),
    canConvertToOpportunity: hasPermission(PERMISSION_CODES.CONVERT_TO_OPPORTUNITY),

    // Warehouse Transfer
    canViewWarehouseDashboard: hasPermission(PERMISSION_CODES.WAREHOUSE_DASHBOARD_VIEW),
    canViewTransferOrder: hasPermission(PERMISSION_CODES.TRANSFER_ORDER_VIEW),
    canViewPdaShippingLogs: hasPermission(PERMISSION_CODES.PDA_SHIPPING_LOG_VIEW),
    canViewTransferExceptionFollowup: hasPermission(
      PERMISSION_CODES.TRANSFER_EXCEPTION_FOLLOWUP_VIEW,
    ),
    canEditTransferExceptionFollowup: hasPermission(
      PERMISSION_CODES.TRANSFER_EXCEPTION_FOLLOWUP_EDIT,
    ),
    canViewTransferPickingProgress: hasPermission(PERMISSION_CODES.TRANSFER_PICKING_PROGRESS_VIEW),
    canCreateEditTransferOrder: hasPermission(PERMISSION_CODES.TRANSFER_ORDER_CREATE_EDIT),
    canDeleteTransferOrder: hasPermission(PERMISSION_CODES.TRANSFER_ORDER_DELETE),
    canManualReceiveTransferOrder: hasPermission(PERMISSION_CODES.TRANSFER_ORDER_MANUAL_RECEIVING),
    canViewTransferShippingSchedule: hasPermission(
      PERMISSION_CODES.TRANSFER_SHIPPING_SCHEDULE_VIEW,
    ),
    canCreateEditTransferShippingSchedule: hasPermission(
      PERMISSION_CODES.TRANSFER_SHIPPING_SCHEDULE_CREATE_EDIT,
    ),
    canViewWarehousePurchaseOrderArrivalSchedule: hasPermission(
      PERMISSION_CODES.WAREHOUSE_PURCHASE_ORDER_ARRIVAL_SCHEDULE_VIEW,
    ),
    canViewInspectionRecord: hasPermission(PERMISSION_CODES.INSPECTION_RECORD_VIEW),
    canCreateEditInspectionRecord: hasPermission(PERMISSION_CODES.INSPECTION_RECORD_CREATE_EDIT),
    canApproveInspectionRecord: hasPermission(PERMISSION_CODES.INSPECTION_RECORD_APPROVE),
    canViewStoreReturn: hasPermission(PERMISSION_CODES.STORE_RETURN_VIEW),
    canApproveStoreReturn: hasPermission(PERMISSION_CODES.STORE_RETURN_APPROVE),
    canViewProductTransferRecord: hasPermission(PERMISSION_CODES.PRODUCT_TRANSFER_RECORD_VIEW),
    canViewTransferProductSearch: hasPermission(PERMISSION_CODES.TRANSFER_PRODUCT_SEARCH_VIEW),
    canViewWarehouseTransferDetail: hasAnyPermission([
      PERMISSION_CODES.TRANSFER_ORDER_VIEW,
      PERMISSION_CODES.STORE_RETURN_VIEW,
    ]),

    // User Management
    canViewUsers: (() => {
      if (
        initialState?.currentUser?.role?.name === 'admin' ||
        initialState?.currentUser?.access === 'admin' ||
        initialState?.currentUser?.role?.code === 'admin'
      ) {
        return true;
      }
      return hasPermission(PERMISSION_CODES.USER_VIEW);
    })(),
    canCreateEditUser: (() => {
      if (
        initialState?.currentUser?.role?.name === 'admin' ||
        initialState?.currentUser?.access === 'admin' ||
        initialState?.currentUser?.role?.code === 'admin'
      ) {
        return true;
      }
      return hasPermission(PERMISSION_CODES.USER_CREATE_EDIT);
    })(),
    canDeleteUser: (() => {
      if (
        initialState?.currentUser?.role?.name === 'admin' ||
        initialState?.currentUser?.access === 'admin' ||
        initialState?.currentUser?.role?.code === 'admin'
      ) {
        return true;
      }
      return hasPermission(PERMISSION_CODES.USER_DELETE);
    })(),
    canViewWholesaleCustomers: (() => {
      if (
        initialState?.currentUser?.role?.name === 'admin' ||
        initialState?.currentUser?.access === 'admin' ||
        initialState?.currentUser?.role?.code === 'admin'
      ) {
        return true;
      }
      return hasPermission(PERMISSION_CODES.WHOLESALE_CUSTOMER_VIEW);
    })(),
    canCreateEditWholesaleCustomers: (() => {
      if (
        initialState?.currentUser?.role?.name === 'admin' ||
        initialState?.currentUser?.access === 'admin' ||
        initialState?.currentUser?.role?.code === 'admin'
      ) {
        return true;
      }
      return hasPermission(PERMISSION_CODES.WHOLESALE_CUSTOMER_CREATE_EDIT);
    })(),
    canDeleteWholesaleCustomers: (() => {
      if (
        initialState?.currentUser?.role?.name === 'admin' ||
        initialState?.currentUser?.access === 'admin' ||
        initialState?.currentUser?.role?.code === 'admin'
      ) {
        return true;
      }
      return hasPermission(PERMISSION_CODES.WHOLESALE_CUSTOMER_DELETE);
    })(),
    canViewTeams: (() => {
      if (
        initialState?.currentUser?.role?.name === 'admin' ||
        initialState?.currentUser?.access === 'admin' ||
        initialState?.currentUser?.role?.code === 'admin'
      ) {
        return true;
      }
      return hasPermission(PERMISSION_CODES.TEAM_VIEW);
    })(),
    canCreateEditTeam: (() => {
      if (
        initialState?.currentUser?.role?.name === 'admin' ||
        initialState?.currentUser?.access === 'admin' ||
        initialState?.currentUser?.role?.code === 'admin'
      ) {
        return true;
      }
      return hasPermission(PERMISSION_CODES.TEAM_CREATE_EDIT);
    })(),
    canDeleteTeam: (() => {
      if (
        initialState?.currentUser?.role?.name === 'admin' ||
        initialState?.currentUser?.access === 'admin' ||
        initialState?.currentUser?.role?.code === 'admin'
      ) {
        return true;
      }
      return hasPermission(PERMISSION_CODES.TEAM_DELETE);
    })(),
    canViewRoles: (() => {
      if (
        initialState?.currentUser?.role?.name === 'admin' ||
        initialState?.currentUser?.access === 'admin' ||
        initialState?.currentUser?.role?.code === 'admin'
      ) {
        return true;
      }
      return hasPermission(PERMISSION_CODES.ROLE_VIEW);
    })(),
    canCreateEditRole: (() => {
      if (
        initialState?.currentUser?.role?.name === 'admin' ||
        initialState?.currentUser?.access === 'admin' ||
        initialState?.currentUser?.role?.code === 'admin'
      ) {
        return true;
      }
      return hasPermission(PERMISSION_CODES.ROLE_CREATE_EDIT);
    })(),
    canDeleteRole: (() => {
      if (
        initialState?.currentUser?.role?.name === 'admin' ||
        initialState?.currentUser?.access === 'admin' ||
        initialState?.currentUser?.role?.code === 'admin'
      ) {
        return true;
      }
      return hasPermission(PERMISSION_CODES.ROLE_DELETE);
    })(),
    canViewProductViewLogs: (() => {
      if (
        initialState?.currentUser?.role?.name === 'admin' ||
        initialState?.currentUser?.access === 'admin' ||
        initialState?.currentUser?.role?.code === 'admin'
      ) {
        return true;
      }
      return hasPermission(PERMISSION_CODES.USER_PRODUCT_VIEW_LOG_VIEW);
    })(),
    canViewNotificationLogs: (() => {
      if (
        initialState?.currentUser?.role?.name === 'admin' ||
        initialState?.currentUser?.access === 'admin' ||
        initialState?.currentUser?.role?.code === 'admin'
      ) {
        return true;
      }
      return hasPermission(PERMISSION_CODES.USER_NOTIFICATION_LOG_VIEW);
    })(),
    canManageMobileUsers: (() => {
      if (
        initialState?.currentUser?.role?.name === 'admin' ||
        initialState?.currentUser?.access === 'admin' ||
        initialState?.currentUser?.role?.code === 'admin'
      ) {
        return true;
      }
      return hasPermission(PERMISSION_CODES.MOBILE_USER_MANAGEMENT_MANAGE);
    })(),
    canViewWarehouseUsers: (() => {
      if (
        initialState?.currentUser?.role?.name === 'admin' ||
        initialState?.currentUser?.access === 'admin' ||
        initialState?.currentUser?.role?.code === 'admin'
      ) {
        return true;
      }
      return hasPermission(PERMISSION_CODES.WAREHOUSE_USERS_MANAGEMENT_VIEW);
    })(),
    canManageWarehouseUserInventoryAdjustment: (() => {
      if (
        initialState?.currentUser?.role?.name === 'admin' ||
        initialState?.currentUser?.access === 'admin' ||
        initialState?.currentUser?.role?.code === 'admin'
      ) {
        return true;
      }
      return hasPermission(PERMISSION_CODES.WAREHOUSE_USERS_MANAGEMENT_INVENTORY_ADJUSTMENT);
    })(),
    canViewHrDashboard: hasPermission(PERMISSION_CODES.HR_DASHBOARD_VIEW),
    canViewHrEmployees:
      hasPermission(PERMISSION_CODES.HR_EMPLOYEES_VIEW) ||
      hasPermission(PERMISSION_CODES.HR_EMPLOYEES_DELETE) ||
      hasPermission(PERMISSION_CODES.HR_EMPLOYEES_MANAGE_ALL),
    canCreateEditHrEmployees: hasPermission(PERMISSION_CODES.HR_EMPLOYEES_CREATE_EDIT),
    canDeleteHrEmployees: hasPermission(PERMISSION_CODES.HR_EMPLOYEES_DELETE),
    canViewHrEmployeesSalary:
      hasPermission(PERMISSION_CODES.HR_EMPLOYEES_SALARY_VIEW) ||
      hasPermission(PERMISSION_CODES.HR_EMPLOYEES_MANAGE_ALL),
    canManageAllHrEmployees: hasPermission(PERMISSION_CODES.HR_EMPLOYEES_MANAGE_ALL),
    canViewHrRoster: hasPermission(PERMISSION_CODES.HR_ROSTER_VIEW),
    canCreateEditHrRoster: hasPermission(PERMISSION_CODES.HR_ROSTER_CREATE_EDIT),
    canCreateEditHrRosterClock: hasPermission(PERMISSION_CODES.HR_ROSTER_CLOCK_CREATE_EDIT),
    canViewHrLeave: hasPermission(PERMISSION_CODES.HR_LEAVE_VIEW),
    canCreateEditHrLeave: hasPermission(PERMISSION_CODES.HR_LEAVE_CREATE_EDIT),
    canCancelHrLeave: hasPermission(PERMISSION_CODES.HR_LEAVE_CANCEL),
    canViewHrSalaries:
      hasPermission(PERMISSION_CODES.HR_SALARIES_VIEW) ||
      hasPermission(PERMISSION_CODES.HR_SALARIES_MANAGE_ALL),
    canCreateEditHrSalaries: hasPermission(PERMISSION_CODES.HR_SALARIES_CREATE_EDIT),
    canManageAllHrSalaries: hasPermission(PERMISSION_CODES.HR_SALARIES_MANAGE_ALL),
    canViewHrSalaryVisibility: hasPermission(PERMISSION_CODES.HR_SALARY_VISIBILITY_VIEW),
    canViewHrAttendanceStats: hasPermission(PERMISSION_CODES.HR_ATTENDANCE_STATS_VIEW),
    canViewHrWorkHourAnomalies:
      hasPermission(PERMISSION_CODES.HR_WORK_HOUR_ANOMALIES_VIEW) ||
      hasPermission(PERMISSION_CODES.HR_WORK_HOUR_ANOMALIES_MANAGE),
    canManageHrWorkHourAnomalies: hasPermission(PERMISSION_CODES.HR_WORK_HOUR_ANOMALIES_MANAGE),
    canViewHrAuditLogs: hasPermission(PERMISSION_CODES.HR_AUDIT_LOGS_VIEW),

    // Route-level access (for menu items)
    // Products module
    productModule: hasModulePermission('productModule', permissions),

    // Purchase module
    purchaseModule: hasAnyPermission([
      PERMISSION_CODES.PURCHASE_PROCUREMENT_GOAL_VIEW,
      PERMISSION_CODES.PURCHASE_PROCUREMENT_GOAL_COMPLETE,
      PERMISSION_CODES.PURCHASE_OPPORTUNITY_VIEW,
      PERMISSION_CODES.AI_PURCHASE_IMPORT_VIEW,
      PERMISSION_CODES.PURCHASE_QUOTE_VIEW,
      PERMISSION_CODES.PURCHASE_QUOTE_APPROVE,
      PERMISSION_CODES.PURCHASE_ORDER_VIEW,
      PERMISSION_CODES.PURCHASE_MARKET_PRICE_SEARCH_VIEW,
      PERMISSION_CODES.PURCHASE_GREENPRICE_CATALOG_VIEW,
      PERMISSION_CODES.PURCHASE_PRICE_COMPETITIVENESS_VIEW,
      PERMISSION_CODES.PURCHASE_ORDER_PAYMENT_VIEW,
      PERMISSION_CODES.PURCHASE_ORDER_PAYMENT_VOUCHER_VIEW,
      PERMISSION_CODES.PURCHASE_ORDER_FINANCE_MANAGEMENT_VIEW,
      PERMISSION_CODES.PURCHASE_ORDER_ARRIVAL_SCHEDULE_FINANCE_RECONCILIATION,
      PERMISSION_CODES.LOGISTICS_FEE_MANAGEMENT_VIEW,
      PERMISSION_CODES.PURCHASE_ORDER_HANDLING_FOLLOWUP_VIEW,
      PERMISSION_CODES.PURCHASE_ORDER_MODIFY_APPROVAL_VIEW,
      PERMISSION_CODES.PURCHASE_ORDER_PAYMENT_VARIANCE_REVIEW_VIEW,
      PERMISSION_CODES.PURCHASE_ORDER_ARRIVAL_SCHEDULE_VIEW,
      PERMISSION_CODES.PURCHASE_CATEGORY_ARRIVAL_CALENDAR_VIEW,
      PERMISSION_CODES.PROCUREMENT_ANALYSIS,
      PERMISSION_CODES.PROCUREMENT_ANALYSIS_DASHBOARD_VIEW,
      PERMISSION_CODES.PROCUREMENT_ANALYSIS_MODULE_VIEW,
      PERMISSION_CODES.PROCUREMENT_ANALYSIS_DATA_ERRORS_VIEW,
      PERMISSION_CODES.PROCUREMENT_ANALYSIS_HISTORY_PERFORMANCE_VIEW,
      PERMISSION_CODES.PROCUREMENT_ANALYSIS_SMALL_TRIAL_SALE_FOLLOWUP_VIEW,
      PERMISSION_CODES.PROCUREMENT_ANALYSIS_OPPORTUNITY_VIEW,
      PERMISSION_CODES.REPLENISHMENT_VIEW,
    ]),

    // Supplier module
    supplierModule: hasAnyPermission([
      PERMISSION_CODES.SUPPLIER_VIEW,
      PERMISSION_CODES.SUPPLIER_PLATFORM_GROUP_VIEW,
      PERMISSION_CODES.SUPPLIER_PLATFORM_MESSAGE_VIEW,
      PERMISSION_CODES.SUPPLIER_PLATFORM_MESSAGE_FOLLOW_UP,
      PERMISSION_CODES.SUPPLIER_FOLLOW_UP_VIEW,
      PERMISSION_CODES.SUPPLIER_FOLLOW_UP_MANAGE,
      PERMISSION_CODES.SUPPLIER_DEVELOPMENT_VIEW,
      PERMISSION_CODES.SUPPLIER_DEVELOPMENT_CREATE_EDIT,
      PERMISSION_CODES.SUPPLIER_CALLS_VIEW,
      PERMISSION_CODES.SUPPLIER_CALLS_CREATE,
      PERMISSION_CODES.SUPPLIER_CALLS_UPDATE_NOTES,
      PERMISSION_CODES.SUPPLIER_CALLS_LISTEN_RECORDING,
    ]),

    // Store module
    storeModule: (() => {
      if (
        initialState?.currentUser?.role?.name === 'admin' ||
        initialState?.currentUser?.access === 'admin' ||
        initialState?.currentUser?.role?.code === 'admin'
      ) {
        return true;
      }
      return hasAnyPermission([
        PERMISSION_CODES.STORE_VIEW,
        PERMISSION_CODES.STORE_RENT_VIEW,
        PERMISSION_CODES.STORE_RENT_MANAGE,
        PERMISSION_CODES.STORE_RENT_PAYMENT_REQUEST,
        PERMISSION_CODES.STORE_RENT_PAYMENT_EXECUTE,
        PERMISSION_CODES.STORE_TARGETS_VIEW,
        PERMISSION_CODES.STORE_TARGETS_EDIT,
        PERMISSION_CODES.CATEGORY_PLACEMENT_VIEW,
        PERMISSION_CODES.PDA_ACCOUNT_VIEW,
        PERMISSION_CODES.PDA_OPERATION_LOG_VIEW,
      ]);
    })(),

    // Inventory module
    inventoryModule: hasAnyPermission([
      PERMISSION_CODES.INVENTORY_OVERVIEW_VIEW,
      PERMISSION_CODES.INVENTORY_HISTORY_VIEW,
      PERMISSION_CODES.INVENTORY_CHANGE_LOG_VIEW,
      PERMISSION_CODES.INVENTORY_TASK_QUEUE_VIEW,
      PERMISSION_CODES.INVENTORY_CHECK_FOLLOWUPS_VIEW,
      PERMISSION_CODES.INVENTORY_LOSS_VIEW,
      PERMISSION_CODES.INVENTORY_FINANCIAL_LOSS_DASHBOARD_VIEW,
      PERMISSION_CODES.PRODUCT_DAMAGE_REPORT_VIEW,
      PERMISSION_CODES.STOCKTAKE_VIEW,
    ]),

    // Operations Management module (Price Change)
    operationsModule:
      hasAnyPermission([
        PERMISSION_CODES.PRICE_CHANGE_VIEW,
        PERMISSION_CODES.PRICE_CHANGE_APPROVE,
        PERMISSION_CODES.PRICE_CHANGE_EXPORT,
      ]) || hasPermission('price_change_management:view'),

    // Price Change Management flags (for components/guards)
    'price_change:view': hasPermission(PERMISSION_CODES.PRICE_CHANGE_VIEW),
    'price_change:approve': hasPermission(PERMISSION_CODES.PRICE_CHANGE_APPROVE),
    'price_change:export': hasPermission(PERMISSION_CODES.PRICE_CHANGE_EXPORT),
    'expired_product_management:view': hasPermission(
      PERMISSION_CODES.EXPIRED_PRODUCT_MANAGEMENT_VIEW,
    ),
    'expired_product_management:unsalable_report_view': hasPermission(
      PERMISSION_CODES.UNSALABLE_PRODUCT_REPORT_VIEW,
    ),
    'expired_product_management:unsalable_report_create': hasPermission(
      PERMISSION_CODES.UNSALABLE_PRODUCT_REPORT_CREATE,
    ),
    'expired_product_management:unsalable_report_follow_up': hasPermission(
      PERMISSION_CODES.UNSALABLE_PRODUCT_REPORT_FOLLOW_UP,
    ),

    // Procurement Analytics module
    procurementAnalyticsModule: hasAnyPermission([
      PERMISSION_CODES.PROCUREMENT_ANALYSIS,
      PERMISSION_CODES.PROCUREMENT_ANALYSIS_DASHBOARD_VIEW,
      PERMISSION_CODES.PROCUREMENT_ANALYSIS_MODULE_VIEW,
      PERMISSION_CODES.PROCUREMENT_ANALYSIS_DATA_ERRORS_VIEW,
      PERMISSION_CODES.PROCUREMENT_ANALYSIS_HISTORY_PERFORMANCE_VIEW,
      PERMISSION_CODES.PROCUREMENT_ANALYSIS_SMALL_TRIAL_SALE_FOLLOWUP_VIEW,
      PERMISSION_CODES.PROCUREMENT_ANALYSIS_OPPORTUNITY_VIEW,
      PERMISSION_CODES.REPLENISHMENT_VIEW,
      PERMISSION_CODES.PURCHASE_CATEGORY_ARRIVAL_CALENDAR_VIEW,
    ]),
    canViewPurchaseOpportunityAnalytics: hasPermission(
      PERMISSION_CODES.PROCUREMENT_ANALYSIS_OPPORTUNITY_VIEW,
    ),
    canViewSmallTrialSaleFollowup: hasPermission(
      PERMISSION_CODES.PROCUREMENT_ANALYSIS_SMALL_TRIAL_SALE_FOLLOWUP_VIEW,
    ),

    // Sales Analysis module
    salesAnalysisModule: hasAnyPermission([
      PERMISSION_CODES.DATA_DASHBOARD_VIEW,
      PERMISSION_CODES.PRODUCT_SALES_ANALYSIS_VIEW,
      PERMISSION_CODES.PRODUCT_SPECIFIC_SALES_VIEW,
      PERMISSION_CODES.GROSS_MARGIN_ANALYSIS_VIEW,
      PERMISSION_CODES.CATEGORY_SALES_VIEW,
      PERMISSION_CODES.CATEGORY_SALES_CHART_VIEW,
      PERMISSION_CODES.PAYMENT_GATEWAY_VIEW,
      PERMISSION_CODES.SUPPLIER_SALES_REPORT_VIEW,
      PERMISSION_CODES.FRANCHISE_STORE_SALES_ANALYSIS_VIEW,
    ]),

    // Data Anomaly module
    dataAnomalyModule: hasAnyPermission([
      PERMISSION_CODES.DATA_CHECKS_VIEW,
      PERMISSION_CODES.BAD_PRODUCT_NAME_VIEW,
      PERMISSION_CODES.CATEGORY_ERRORS_VIEW,
      PERMISSION_CODES.MISSING_CATEGORIES_VIEW,
      PERMISSION_CODES.MISSING_BRAND_VIEW,
      PERMISSION_CODES.MISSING_DESCRIPTION_VIEW,
      PERMISSION_CODES.DUPLICATE_PRODUCTS_MANAGE,
      PERMISSION_CODES.DUPLICATE_BARCODE_VIEW,
    ]),

    // Script module
    scriptModule: hasAnyPermission([
      PERMISSION_CODES.SCRIPT_VIEW,
      PERMISSION_CODES.SCRIPT_EXECUTION_LOG_VIEW,
    ]),

    // POS module
    posModule: hasAnyPermission([
      PERMISSION_CODES.STORE_ACCOUNT_VIEW,
      PERMISSION_CODES.PAYMENT_METHOD_VIEW,
      PERMISSION_CODES.POS_APP_VERSION_VIEW,
      PERMISSION_CODES.POS_SESSION_VIEW,
      PERMISSION_CODES.POS_OPERATION_LOG_VIEW,
      PERMISSION_CODES.POS_QUICK_ACTION_VIEW,
      PERMISSION_CODES.POS_BEA_CONFIG_VIEW,
      PERMISSION_CODES.POS_BEA_CONFIG_EDIT,
    ]),

    onlineStoreModule: hasAnyPermission([
      PERMISSION_CODES.ONLINE_PRODUCT_VIEW,
      PERMISSION_CODES.ONLINE_PRODUCT_CREATE,
      PERMISSION_CODES.ONLINE_PRODUCT_APPROVE,
      PERMISSION_CODES.ONLINE_ORDER_VIEW,
      PERMISSION_CODES.ONLINE_ORDER_EDIT,
      PERMISSION_CODES.ONLINE_ORDER_CASH_REFUND,
      PERMISSION_CODES.ONLINE_ORDER_ORIGINAL_REFUND,
      PERMISSION_CODES.ONLINE_ORDER_FOLLOWUP_VIEW,
      PERMISSION_CODES.ONLINE_STORE_BEHAVIOUR_VIEW,
      PERMISSION_CODES.ONLINE_STORE_DISCOUNT_CODE_VIEW,
      PERMISSION_CODES.ONLINE_STORE_CUSTOMER_MESSAGE_VIEW,
      PERMISSION_CODES.ONLINE_STORE_BLOG_POST_VIEW,
      PERMISSION_CODES.ONLINE_STORE_BLOG_POST_CREATE_EDIT,
      PERMISSION_CODES.ONLINE_STORE_BLOG_POST_DELETE,
    ]),

    marketingManagementModule: hasAnyPermission([
      PERMISSION_CODES.MARKETING_MANAGEMENT_VIEW,
      PERMISSION_CODES.MARKETING_MANAGEMENT_MANAGE,
    ]),

    // Store Operations (門店營運管理) - visible if user has any related permission
    storeOperationsModule: hasAnyPermission([
      'store_tasks_management:view',
      'store_tasks_management:edit',
      'store_tasks_management:audit',
      PERMISSION_CODES.STORE_INSPECTION_QUESTION_MANAGEMENT_VIEW,
      PERMISSION_CODES.STORE_INSPECTION_REPORTS_VIEW,
      'store_support_tickets:view',
      'store_support_tickets:reply_archive',
      'customer_complaints:view',
      'roaster_records:view',
      PERMISSION_CODES.STORE_TUTORIAL_VIEW,
      PERMISSION_CODES.STORE_SUPPORT_CONTACTS_VIEW,
      PERMISSION_CODES.PDA_DAILY_INVENTORY_CHECK_VIEW,
    ]),

    // Store Tasks permissions (門店任務管理) & new pages
    'store_tasks_management:view': hasPermission('store_tasks_management:view'),
    'store_tasks_management:edit': hasPermission('store_tasks_management:edit'),
    'store_tasks_management:audit': hasPermission('store_tasks_management:audit'),
    'store_support_tickets:view': hasPermission('store_support_tickets:view'),
    'store_support_tickets:reply_archive': hasPermission('store_support_tickets:reply_archive'),
    'customer_complaints:view': hasPermission('customer_complaints:view'),
    'roaster_records:view': hasPermission('roaster_records:view'),
    'roaster_records:export': hasPermission('roaster_records:export'),
    'store_tutorials:view': hasPermission(PERMISSION_CODES.STORE_TUTORIAL_VIEW),
    'store_tutorials:create_edit': hasPermission(PERMISSION_CODES.STORE_TUTORIAL_CREATE_EDIT),
    'store_tutorials:delete': hasPermission(PERMISSION_CODES.STORE_TUTORIAL_DELETE),
    'store_inspection_question_management:view': hasPermission(
      PERMISSION_CODES.STORE_INSPECTION_QUESTION_MANAGEMENT_VIEW,
    ),
    'store_inspection_question_management:create_edit': hasPermission(
      PERMISSION_CODES.STORE_INSPECTION_QUESTION_MANAGEMENT_CREATE_EDIT,
    ),
    'store_inspection_reports:view': hasPermission(PERMISSION_CODES.STORE_INSPECTION_REPORTS_VIEW),
    'store_inspection_reports:review': hasPermission(
      PERMISSION_CODES.STORE_INSPECTION_REPORTS_REVIEW,
    ),
    'store_bonus:view': hasPermission(PERMISSION_CODES.STORE_BONUS_VIEW),
    'store_bonus:manage': hasPermission(PERMISSION_CODES.STORE_BONUS_MANAGE),
    'store_bonus:finalize': hasPermission(PERMISSION_CODES.STORE_BONUS_FINALIZE),
    canViewStoreSupportContacts: hasPermission(PERMISSION_CODES.STORE_SUPPORT_CONTACTS_VIEW),
    canCreateEditStoreSupportContacts: hasPermission(
      PERMISSION_CODES.STORE_SUPPORT_CONTACTS_CREATE_EDIT,
    ),
    canDeleteStoreSupportContacts: hasPermission(PERMISSION_CODES.STORE_SUPPORT_CONTACTS_DELETE),
    'pda_daily_inventory_check:view': hasPermission(
      PERMISSION_CODES.PDA_DAILY_INVENTORY_CHECK_VIEW,
    ),
    'pda_daily_inventory_check:create_edit': hasPermission(
      PERMISSION_CODES.PDA_DAILY_INVENTORY_CHECK_CREATE_EDIT,
    ),
    'pda_daily_inventory_check:delete': hasPermission(
      PERMISSION_CODES.PDA_DAILY_INVENTORY_CHECK_DELETE,
    ),

    // Market Info module
    marketInfoModule: hasAnyPermission([
      PERMISSION_CODES.COLLECTION_TASK_VIEW,
      PERMISSION_CODES.MI_PRODUCT_VIEW,
      PERMISSION_CODES.DATA_REVIEW_VIEW,
      PERMISSION_CODES.DATA_ANALYSIS_VIEW,
      PERMISSION_CODES.PRICE_ANALYSIS_VIEW,
      PERMISSION_CODES.MARKET_PRODUCT_ANALYSIS_VIEW,
      PERMISSION_CODES.MI_USER_VIEW,
      PERMISSION_CODES.CONVERT_TO_OPPORTUNITY,
    ]),

    // Warehouse Transfer module
    warehouseTransferModule: hasAnyPermission([
      PERMISSION_CODES.PDA_SHIPPING_LOG_VIEW,
      PERMISSION_CODES.INVENTORY_ANOMALY_DASHBOARD_VIEW,
      PERMISSION_CODES.WAREHOUSE_DASHBOARD_VIEW,
      PERMISSION_CODES.TRANSFER_ORDER_VIEW,
      PERMISSION_CODES.TRANSFER_EXCEPTION_FOLLOWUP_VIEW,
      PERMISSION_CODES.INSPECTION_RECORD_VIEW,
      PERMISSION_CODES.PRODUCT_TRANSFER_RECORD_VIEW,
      PERMISSION_CODES.TRANSFER_PRODUCT_SEARCH_VIEW,
      PERMISSION_CODES.TRANSFER_SHIPPING_SCHEDULE_VIEW,
      PERMISSION_CODES.WAREHOUSE_PURCHASE_ORDER_ARRIVAL_SCHEDULE_VIEW,
      PERMISSION_CODES.STORE_RETURN_VIEW,
      PERMISSION_CODES.WAREHOUSE_PURCHASE_RECEIPT_VIEW,
    ]),

    // User Management module
    userManagementModule: (() => {
      // 暫時為admin用戶強制啟用所有權限
      if (
        initialState?.currentUser?.role?.name === 'admin' ||
        initialState?.currentUser?.access === 'admin' ||
        initialState?.currentUser?.role?.code === 'admin'
      ) {
        console.log('[Access Control] Admin user detected, granting full user management access');
        return true;
      }

      const requiredPermissions = [
        PERMISSION_CODES.USER_VIEW,
        PERMISSION_CODES.TEAM_VIEW,
        PERMISSION_CODES.ROLE_VIEW,
        PERMISSION_CODES.AUDIT_VIEW,
        PERMISSION_CODES.USER_PRODUCT_VIEW_LOG_VIEW,
        PERMISSION_CODES.USER_NOTIFICATION_LOG_VIEW,
        PERMISSION_CODES.MOBILE_USER_MANAGEMENT_MANAGE,
        PERMISSION_CODES.WAREHOUSE_USERS_MANAGEMENT_VIEW,
        // Include franchise account permissions so franchise managers can see the group
        PERMISSION_CODES.FRANCHISEE_MANAGEMENT_ACCOUNT_VIEW,
        PERMISSION_CODES.FRANCHISEE_MANAGEMENT_ACCOUNT_VIEW_LEGACY,
        PERMISSION_CODES.FRANCHISEE_ACCOUNT_VIEW,
      ];

      const hasAccess = hasAnyPermission(requiredPermissions);

      if (process.env.NODE_ENV === 'development') {
        console.debug('[Access Control] User Management Module Check:');
        console.debug('  Required permissions:', requiredPermissions);
        console.debug('  Available permissions:', permissions);
        console.debug('  Has access:', hasAccess);

        // 檢查每個權限
        requiredPermissions.forEach((permission) => {
          const hasThisPermission = hasPermission(permission);
          console.debug(`  ${permission}: ${hasThisPermission ? '✅' : '❌'}`);
        });
      }

      return hasAccess;
    })(),

    hrManagementModule: hasAnyPermission([
      PERMISSION_CODES.HR_DASHBOARD_VIEW,
      PERMISSION_CODES.HR_EMPLOYEES_VIEW,
      PERMISSION_CODES.HR_EMPLOYEES_CREATE_EDIT,
      PERMISSION_CODES.HR_EMPLOYEES_DELETE,
      PERMISSION_CODES.HR_EMPLOYEES_MANAGE_ALL,
      PERMISSION_CODES.HR_ROSTER_VIEW,
      PERMISSION_CODES.HR_ROSTER_CREATE_EDIT,
      PERMISSION_CODES.HR_ROSTER_CLOCK_CREATE_EDIT,
      PERMISSION_CODES.HR_LEAVE_VIEW,
      PERMISSION_CODES.HR_LEAVE_CREATE_EDIT,
      PERMISSION_CODES.HR_LEAVE_CANCEL,
      PERMISSION_CODES.HR_SALARIES_VIEW,
      PERMISSION_CODES.HR_SALARIES_CREATE_EDIT,
      PERMISSION_CODES.HR_SALARIES_MANAGE_ALL,
      PERMISSION_CODES.HR_SALARY_VISIBILITY_VIEW,
      PERMISSION_CODES.HR_ATTENDANCE_STATS_VIEW,
      PERMISSION_CODES.HR_WORK_HOUR_ANOMALIES_VIEW,
      PERMISSION_CODES.HR_WORK_HOUR_ANOMALIES_MANAGE,
      PERMISSION_CODES.HR_AUDIT_LOGS_VIEW,
    ]),

    // Franchise Management module
    franchiseeManagementModule: hasAnyPermission([
      // Support both code styles to match backend/shared and legacy
      PERMISSION_CODES.FRANCHISEE_MANAGEMENT_ACCOUNT_VIEW,
      PERMISSION_CODES.FRANCHISEE_MANAGEMENT_ACCOUNT_VIEW_LEGACY,
      PERMISSION_CODES.FRANCHISEE_ACCOUNT_VIEW,
    ]),

    // Inventory Analysis module
    inventory_analysis: [
      canViewWarehouseStructureAnalysis,
      canViewStoreStructureAnalysis,
      canViewInventoryExpiryAnalysis,
      canViewInventoryStoreAnalysis,
      canViewInventoryUnsoldProducts,
      canExportInventoryUnsoldProducts,
      canViewInventoryTurnoverAnalysis,
    ].some(Boolean),

    // Member Management module
    memberManagementModule: hasAnyPermission([
      PERMISSION_CODES.MEMBER_VIEW,
      PERMISSION_CODES.MEMBER_COUPON_VIEW,
      PERMISSION_CODES.COUPON_TEMPLATE_VIEW,
    ]),
    canViewMembers: hasPermission(PERMISSION_CODES.MEMBER_VIEW),
    canCreateEditMember: hasPermission(PERMISSION_CODES.MEMBER_CREATE_EDIT),
    canDeleteMember: hasPermission(PERMISSION_CODES.MEMBER_DELETE),
    canExportMembers: hasPermission(PERMISSION_CODES.MEMBER_EXPORT),
    canViewMemberVerification: hasPermission(PERMISSION_CODES.MEMBER_VERIFICATION_VIEW),
    canViewMemberChallenge: hasPermission(PERMISSION_CODES.MEMBER_CHALLENGE_VIEW),
    canViewMemberCoupon: hasPermission(PERMISSION_CODES.MEMBER_COUPON_VIEW),
    canCreateEditMemberCoupon: hasPermission(PERMISSION_CODES.MEMBER_COUPON_CREATE_EDIT),
    canViewCouponTemplate: hasPermission(PERMISSION_CODES.COUPON_TEMPLATE_VIEW),
    canCreateEditCouponTemplate: hasPermission(PERMISSION_CODES.COUPON_TEMPLATE_CREATE_EDIT),

    // Admin-only features
    isAdmin: initialState?.currentUser?.access === 'admin',
  };
}
